// filepath: /home/hisham/Documents/TMO/crm/toolmancrm/src/components/users/RecentOrders.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Grid, Typography, Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress, TextField, Autocomplete, Button, MenuItem,
  Rating,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import AirlineSeatReclineExtraIcon from '@mui/icons-material/AirlineSeatReclineExtra';

const STATUS_OPTIONS = [
  "pending", "cancelled", "in-progress", "not-available", "completed"
];

const formatDate = (dateStr) => {
  console.log(dateStr);
  if (!dateStr) return '-';
  const utcDate = new Date(dateStr);
  const day = String(utcDate.getDate()).padStart(2, '0');
  const month = String(utcDate.getMonth() + 1).padStart(2, '0');
  const year = utcDate.getFullYear();
  const time = utcDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${day}-${month}-${year} ${time}`;
};

const RecentOrders = ({ userId, isPending, carId, isdescrepancies }) => {
  const [expanded, setExpanded] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [editWasherIdx, setEditWasherIdx] = useState(null);
  const [washerValue, setWasherValue] = useState('');
  const [washerLoading, setWasherLoading] = useState(false);
  const [editWasherOptions, setEditWasherOptions] = useState([]);
  const [editWasher, setEditWasher] = useState(null);
  const [editWasherQuery, setEditWasherQuery] = useState('');
  const editWasherDebounceRef = useRef();
  const [editWasherLoading, setEditWasherLoading] = useState(false);
  const [editDateIdx, setEditDateIdx] = useState(null);
  const [editDateValue, setEditDateValue] = useState('');
  const [editDateOpen, setEditDateOpen] = useState(false);
  const [editStatusIdx, setEditStatusIdx] = useState(null);
  const [editStatusValue, setEditStatusValue] = useState('');
  const [editStatusOpen, setEditStatusOpen] = useState(false);
  // review dialog state
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewOrder, setReviewOrder] = useState(null);

  useEffect(() => {
    if (editWasherIdx) {
      fetchWashers();
    }
    // eslint-disable-next-line
  }, [editWasherIdx]);

  const fetchWashers = (search) => {
    setWasherLoading(true);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/washers/options?search=${encodeURIComponent(search)}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(result => {
        setEditWasherOptions(result || []);
        setWasherLoading(false);
      })
      .catch(() => {
        setEditWasherOptions([]);
        setWasherLoading(false);
      });
  };

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded && !loadedOnce) {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
      fetch(
        `${process.env.REACT_APP_SERVER_URI}/washes/users/${userId}?carId=${carId}&status=${isPending ? 'pending' : isdescrepancies? 'not-available, interior-pending,':'completed,interior-pending'}&limit=5&sort=${encodeURIComponent(JSON.stringify({date:isPending? 1 : -1}))}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      )
        .then(res => res.json())
        .then(data => {
          setOrders(Array.isArray(data.data) ? data.data : []);
          setLoading(false);
          setLoadedOnce(true);
        })
        .catch(() => {
          setOrders([]);
          setLoading(false);
          setLoadedOnce(true);
        });
    }
  };

  const handleEditWasher = (idx, currentWasherId) => {
    setEditWasherIdx(idx);
    setWasherValue(currentWasherId || '');
    fetchWashers();
  };

  const fetchEditWasherOptions = (search = '') => {
    setEditWasherLoading(true);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/washers/options?search=${encodeURIComponent(search)}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(result => {
        setEditWasherOptions(result || []);
        setEditWasherLoading(false);
      })
      .catch(() => {
        setEditWasherOptions([]);
        setEditWasherLoading(false);
      });
  };

  const handleEditWasherInputChange = (event, value) => {
    setEditWasherQuery(value);
    if (editWasherDebounceRef.current) clearTimeout(editWasherDebounceRef.current);
    editWasherDebounceRef.current = setTimeout(() => {
      fetchEditWasherOptions(value);
    }, 400);
  };

  const updateWashField = async (washId, updateObj, onSuccess) => {
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/washes/update/${washId}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateObj),
      });
      await res.json();
      if (onSuccess) onSuccess();
    } catch {
      alert('Failed to update wash.');
    }
  };

  const handleEditWasherSave = () => {
    updateWashField(editWasherIdx, { washerId: editWasher._id, washerName: editWasher.name }, () => {
      setEditWasherIdx(null);
      setEditWasher(null);
      setEditWasherQuery('');
      setEditWasherOptions([]);
    });
  };

  const handleEditDateOpen = (idx, date) => {
    setEditDateIdx(idx);
    if (date) {
      const utcDate = new Date(date);
      if (utcDate.getHours() === 0 && utcDate.getMinutes() === 0 && utcDate.getSeconds() === 0) {
        utcDate.setHours(9, 0, 0, 0);
      }
      const year = utcDate.getFullYear();
      const month = String(utcDate.getMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getDate()).padStart(2, '0');
      const hours = String(utcDate.getHours()).padStart(2, '0');
      const minutes = String(utcDate.getMinutes()).padStart(2, '0');
      setEditDateValue(`${year}-${month}-${day}T${hours}:${minutes}`);
    } else {
      setEditDateValue('');
    }
    setEditDateOpen(true);
  };

  const handleEditDateSave = async () => {
    const idx = editDateIdx;
    const order = orders.find((o, i) => (o._id || i) === idx || i === idx);
    setEditDateOpen(false);
    setEditDateIdx(null);
    const [datePart, timePart] = editDateValue.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    const istDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
    setOrders(orders =>
      orders.map((o, i) =>
        (o._id || i) === idx || i === idx ? { ...o, date: istDate.toISOString() } : o
      )
    );
    try {
      const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
      await fetch(`${process.env.REACT_APP_SERVER_URI}/washes/update-datetime/${order._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ date: istDate.toISOString() }),
      });
    } catch (err) {
      alert('Failed to update date');
    }
    setEditDateValue('');
  };

  const handleEditStatusOpen = (idx, status) => {
    setEditStatusIdx(idx);
    setEditStatusValue(status || '');
    setEditStatusOpen(true);
  };

  const handleEditStatusSave = async () => {
    const idx = editStatusIdx;
    if (idx === null || idx === undefined) return;

    const order = orders[idx];
    if (!order) {
      alert('Order not found');
      setEditStatusIdx(null);
      setEditStatusOpen(false);
      setEditStatusValue('');
      return;
    }

    const prevStatus = order.status;
    setOrders(prev =>
      prev.map((o, i) => (i === idx ? { ...o, status: editStatusValue } : o))
    );

    try {
      if (!order._id) throw new Error('Order id missing');
      const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
      const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/washes/update/${order._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ status: editStatusValue }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      const updated = await res.json();
      if (updated && updated._id) {
        setOrders(prev => prev.map(o => (o._id === updated._id ? updated : o)));
      }
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Failed to update status');
      setOrders(prev =>
        prev.map((o, i) => (i === idx ? { ...o, status: prevStatus } : o))
      );
    } finally {
      setEditStatusIdx(null);
      setEditStatusOpen(false);
      setEditStatusValue('');
    }
  };

  const handleFeedbackClick = (order) => {
    setReviewOrder(order);
    setReviewOpen(true);
  };

  return (
    <Grid item size={12} container spacing={2} sx={{ mt: 1, width: '100%' }}>
      <Accordion
        variant="outlined"
        expanded={expanded}
        onChange={handleAccordionChange}
        sx={{ width: '100%' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">{isPending ? 'Pending' : isdescrepancies? "Discrepancies" : 'Completed'} Washes</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 2 }}>Loading recent washes...</Box>
          ) : orders.length > 0 ? (
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small" sx={{ width: '100%', minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>...</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Car</TableCell>
                    <TableCell>Washer</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order, idx) => (
                    <TableRow key={order._id || idx}>
                      <TableCell>
                        <IconButton
                          onClick={async () => {
                            const confirm = window.confirm(order.interior ? 'Unmark interior wash' : 'Mark interior wash');
                            if (!confirm) return;
                            const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
                            try {
                              await fetch(`${process.env.REACT_APP_SERVER_URI}/washes/update/${order._id}`, {
                                method: 'POST',
                                headers: {
                                  Authorization: token ? `Bearer ${token}` : '',
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({ interior: !order.interior }),
                              });
                              setOrders(ws =>
                                ws.map((w, i) =>
                                  (w._id || i) === (order._id || idx)
                                    ? { ...w, interior: !w.interior }
                                    : w
                                )
                              );
                              alert(order.interior ? 'Interior wash unmarked!' : 'Interior wash marked!');
                            } catch {
                              alert('Failed to update interior status.');
                            }
                          }}
                        >
                          <AirlineSeatReclineExtraIcon
                            fontSize="large"
                            sx={{
                              color: order.interior ? 'text.primary' : 'rgba(0,0,0,0.15)'
                            }}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {order.date ? formatDate(order.date) : '-'}
                        </Box>
                      </TableCell>
                      <TableCell>{order.carRegNo || order.car?.regNo || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {order.washerName || order.washer?.name || '-'}
                          <IconButton
                            color="primary"
                            size="small"
                            sx={{ ml: 1 }}
                            onClick={() => handleEditWasher(order._id, order.washer?._id || order.washer)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {order.type && (
                          <Chip
                            label={order.type}
                            size="small"
                            color={order.type === 'subscription' ? 'primary' : 'default'}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }} onClick={() => handleFeedbackClick(order)}>
                          <Rating value={order.rating || 0} readOnly size="small" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {order.status && (
                            <Chip
                              label={order.status === "cancelled" ? "Missed" : order.status}
                              size="small"
                              color={order.status === 'completed' ? 'success' : order.status === "cancelled" ? 'error' : 'default'}
                            />
                          )}
                          <IconButton
                            color="primary"
                            size="small"
                            sx={{ ml: 1 }}
                            onClick={() => handleEditStatusOpen(idx, order.status)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {order.notes || '-'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>No completed Washes found.</Box>
          )}
        </AccordionDetails>
      </Accordion>
      <Dialog open={Boolean(editWasherIdx)} onClose={() => setEditWasherIdx(false)}>
        <DialogTitle>Change Washer</DialogTitle>
        <Box px={3} py={2}>
          <Autocomplete
            options={editWasherOptions}
            getOptionLabel={option => option.name || ''}
            loading={washerLoading}
            value={editWasher}
            onChange={(event, value) => setEditWasher(value)}
            inputValue={editWasherQuery}
            onInputChange={handleEditWasherInputChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Washer"
                margin="normal"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {washerLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button onClick={() => setEditWasherIdx(false)} sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleEditWasherSave} variant="contained" color="primary" disabled={!editWasher}>
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Dialog open={editStatusOpen} onClose={() => setEditStatusOpen(false)}>
        <DialogTitle>Edit Status</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Status"
            value={editStatusValue === "cancelled" ? "Missed" : editStatusValue}
            onChange={e => setEditStatusValue(e.target.value === "Missed" ? "cancelled" : e.target.value)}
            fullWidth
            margin="normal"
          >
            {STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt} value={opt}>{opt === "cancelled" ? "Missed" : opt}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditStatusOpen(false)}>Cancel</Button>
          <Button onClick={handleEditStatusSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)}>
        <DialogTitle>Review</DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="subtitle1">Rating:</Typography>
            <Rating value={reviewOrder?.rating || 0} readOnly size="large" />
          </Box>
          <Box mt={2}>
            <Typography variant="subtitle1">Review:</Typography>
            <Typography>{reviewOrder?.review || 'No review available'}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default RecentOrders;